import { useEffect, useEffectEvent, useRef, useState } from "react";
import { FormNotice } from "@/components/forms/FormNotice";
import { loadRecaptchaScript, recaptchaSiteKey } from "@/lib/recaptcha";
import { cn } from "@/lib/utils";

const CAPTCHA_WIDTH = 304;
const CAPTCHA_HEIGHT = 78;

type RecaptchaFieldProps = {
  className?: string;
  invalid?: boolean;
  onTokenChange: (token: string | null) => void;
};

export function RecaptchaField({
  className,
  invalid = false,
  onTokenChange,
}: RecaptchaFieldProps) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [widgetScale, setWidgetScale] = useState(1);
  const emitTokenChange = useEffectEvent(onTokenChange);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const updateScale = () => {
      const availableWidth = frame.clientWidth;
      if (!availableWidth) return;

      const nextScale = Math.min(1, availableWidth / CAPTCHA_WIDTH);
      setWidgetScale((current) =>
        Math.abs(current - nextScale) > 0.01 ? nextScale : current,
      );
    };

    updateScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScale);
      return () => window.removeEventListener("resize", updateScale);
    }

    const resizeObserver = new ResizeObserver(() => updateScale());
    resizeObserver.observe(frame);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    emitTokenChange(null);

    if (!recaptchaSiteKey) {
      setLoadError("reCAPTCHA is unavailable right now. Please try again later.");
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    let isActive = true;
    setLoadError(null);

    void loadRecaptchaScript()
      .then(() => {
        if (!isActive || !containerRef.current || !window.grecaptcha?.render) {
          return;
        }

        window.grecaptcha.ready(() => {
          if (!isActive || !containerRef.current || !window.grecaptcha?.render) {
            return;
          }

          containerRef.current.innerHTML = "";
          window.grecaptcha.render(containerRef.current, {
            sitekey: recaptchaSiteKey,
            callback: (token: string) => emitTokenChange(token),
            "expired-callback": () => emitTokenChange(null),
            "error-callback": () => emitTokenChange(null),
          });
        });
      })
      .catch(() => {
        if (!isActive) return;
        setLoadError("We couldn't load reCAPTCHA. Please refresh and try again.");
        emitTokenChange(null);
      });

    return () => {
      isActive = false;
      emitTokenChange(null);
      container.innerHTML = "";
    };
  }, []);

  return (
    <div
      className={cn(
        "flex w-full justify-center",
        className,
      )}
    >
      <div
        className={cn(
          "w-full max-w-[22rem] rounded-[1.2rem] border bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(250,246,240,0.96))] p-3 shadow-[inset_0_1px_rgba(255,255,255,0.7)]",
          invalid
            ? "border-[rgb(118_42_92_/_18%)] ring-1 ring-[rgb(118_42_92_/_10%)]"
            : "border-[rgb(118_42_92_/_8%)]",
        )}
      >
        {loadError ? (
          <FormNotice
            className="shadow-none"
            message={loadError}
          />
        ) : (
          <div ref={frameRef} className="w-full overflow-visible">
            <div
              className="relative mx-auto transition-[width,height] duration-200 ease-out"
              style={{
                width: `${CAPTCHA_WIDTH * widgetScale}px`,
                height: `${CAPTCHA_HEIGHT * widgetScale}px`,
              }}
            >
              <div
                ref={containerRef}
                className="absolute left-0 top-0 origin-top-left"
                style={{
                  width: `${CAPTCHA_WIDTH}px`,
                  height: `${CAPTCHA_HEIGHT}px`,
                  transform: `scale(${widgetScale})`,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
